import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { adminBanService } from '../../../services/banService';
import type { SuspiciousEntry, SuspiciousType } from '../../../types/ban';

const TYPE_LABELS: Record<SuspiciousType, string> = {
  asn: 'ASN',
  isp: 'ISP',
  domain: 'Domain',
};

const TYPE_COLORS: Record<SuspiciousType, string> = {
  asn: 'bg-blue-100 text-blue-800',
  isp: 'bg-purple-100 text-purple-800',
  domain: 'bg-orange-100 text-orange-800',
};

interface EntryModalProps {
  entry: SuspiciousEntry | null;
  onClose: () => void;
  onSubmit: (data: { type: SuspiciousType; value: string; notes: string }) => void;
  isSubmitting: boolean;
}

function EntryModal({ entry, onClose, onSubmit, isSubmitting }: EntryModalProps) {
  const [type, setType] = useState<SuspiciousType>(entry?.type ?? 'asn');
  const [value, setValue] = useState(entry?.value ?? '');
  const [notes, setNotes] = useState(entry?.notes ?? '');

  const placeholders: Record<SuspiciousType, string> = {
    asn: 'e.g. AS12345 or 12345',
    isp: 'e.g. Hetzner Online GmbH',
    domain: 'e.g. suspicious-cdn.net',
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!value.trim()) return;
    onSubmit({ type, value: value.trim(), notes: notes.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {entry ? 'Edit Watchlist Entry' : 'Add to Watchlist'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {!entry && (
            <div>
              <label className="label">Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as SuspiciousType)}
                className="input"
              >
                <option value="asn">ASN</option>
                <option value="isp">ISP</option>
                <option value="domain">Domain</option>
              </select>
            </div>
          )}
          <div>
            <label className="label">Value</label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholders[type]}
              className="input"
              required
            />
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why is this suspicious? What was observed?"
              className="input min-h-[80px] resize-y"
              rows={3}
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="btn btn-primary">
              {isSubmitting ? 'Saving…' : entry ? 'Save Changes' : 'Add to Watchlist'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function WatchlistTab() {
  const queryClient = useQueryClient();
  const [filterType, setFilterType] = useState<SuspiciousType | ''>('');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState<SuspiciousEntry | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'suspicious', { filterType, search }],
    queryFn: () =>
      adminBanService.getSuspiciousEntries({
        type: filterType || undefined,
        search: search || undefined,
      }),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['admin', 'suspicious'] });

  const createMutation = useMutation({
    mutationFn: adminBanService.createSuspiciousEntry,
    onSuccess: () => { invalidate(); toast.success('Added to watchlist'); setShowModal(false); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to add'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      adminBanService.updateSuspiciousEntry(id, data),
    onSuccess: () => { invalidate(); toast.success('Entry updated'); setEditingEntry(null); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: adminBanService.deleteSuspiciousEntry,
    onSuccess: () => { invalidate(); toast.success('Entry removed'); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to remove'),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: () => adminBanService.bulkDeleteSuspicious(selectedIds),
    onSuccess: (r) => { invalidate(); toast.success(r.message); setSelectedIds([]); },
    onError: (e: any) => toast.error(e.response?.data?.message || 'Failed to delete'),
  });

  const entries = data?.data ?? [];

  const toggleSelect = (id: number) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );

  const allSelected = entries.length > 0 && selectedIds.length === entries.length;
  const toggleAll = () =>
    setSelectedIds(allSelected ? [] : entries.map((e) => e.id));

  const handleDelete = (id: number) => {
    if (window.confirm('Remove this entry from the watchlist?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleBulkDelete = () => {
    if (window.confirm(`Remove ${selectedIds.length} entries from watchlist?`)) {
      bulkDeleteMutation.mutate();
    }
  };

  return (
    <div className="space-y-4">
      {/* Description */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3">
        <svg className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 2a8 8 0 100 16A8 8 0 0010 2zm1 11H9v-2h2v2zm0-4H9V6h2v3z" />
        </svg>
        <p className="text-sm text-amber-800">
          Watchlist stores suspicious ASNs, ISPs, and domains for future analysis. Entries here do <strong>not</strong> block traffic — use the Ban tabs to block.
        </p>
      </div>

      {/* Filters & Add */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <input
          type="text"
          placeholder="Search value or notes…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input w-full sm:max-w-xs"
        />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as SuspiciousType | '')}
          className="input w-full sm:w-36"
        >
          <option value="">All types</option>
          <option value="asn">ASN</option>
          <option value="isp">ISP</option>
          <option value="domain">Domain</option>
        </select>
        <button
          onClick={() => setShowModal(true)}
          className="btn btn-primary sm:ml-auto"
        >
          + Add to Watchlist
        </button>
      </div>

      {/* Bulk actions */}
      {selectedIds.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
          <span className="text-sm font-medium text-amber-900">{selectedIds.length} selected</span>
          <button
            onClick={handleBulkDelete}
            className="btn btn-danger btn-sm"
            disabled={bulkDeleteMutation.isPending}
          >
            Delete Selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 w-10">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} className="rounded" />
              </th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Value</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Notes</th>
              <th className="px-4 py-3 text-left font-medium text-gray-600">Added</th>
              <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading…</td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                  No watchlist entries yet
                </td>
              </tr>
            ) : (
              entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(entry.id)}
                      onChange={() => toggleSelect(entry.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${TYPE_COLORS[entry.type]}`}>
                      {TYPE_LABELS[entry.type]}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-gray-900">{entry.value}</td>
                  <td className="px-4 py-3 text-gray-500 max-w-xs truncate" title={entry.notes ?? ''}>
                    {entry.notes || <span className="text-gray-300 italic">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                    {new Date(entry.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEditingEntry(entry)}
                        className="p-1.5 rounded-md text-blue-500 hover:bg-blue-50 transition"
                        title="Edit notes"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition"
                        title="Remove"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Total count */}
      {data && data.total > 0 && (
        <p className="text-xs text-gray-400">{data.total} total entries</p>
      )}

      {/* Modal */}
      {(showModal || editingEntry) && (
        <EntryModal
          entry={editingEntry}
          onClose={() => { setShowModal(false); setEditingEntry(null); }}
          onSubmit={(formData) => {
            if (editingEntry) {
              updateMutation.mutate({ id: editingEntry.id, data: { value: formData.value, notes: formData.notes } });
            } else {
              createMutation.mutate(formData);
            }
          }}
          isSubmitting={createMutation.isPending || updateMutation.isPending}
        />
      )}
    </div>
  );
}
